import { Col, Row } from '@portalterreno/react-ui-core/Grid';
import Portal from '@portalterreno/react-ui-core/Portal';
import PhoneIcon from '@portalterreno/react-ui-icons/PhoneIcon';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  setIsMobileModalOpen,
  setMobileModal
} from '../../../redux/slices/contactForm';
import { RootState } from '../../../redux/store';
import { WhatsappContactProps } from '../WhatsappContact';
import Button from './Button';
import ButtonWrapper from './ButtonWrapper';
import CampaignBottomBar, { CampaignBottomBarProps } from './CampaignBottomBar';
import ContactModal from './ContactModal';
import PhoneButton from './PhoneButton';
import ShowPhoneModal, { ShowPhoneModalProps } from './ShowPhoneModal';
import WhatsappButton from './WhatsappButton';
import WhatsappModal, { WhatsappModalProps } from './WhatsappModal';

export type MobileContactFormProps = Pick<
  WhatsappModalProps,
  'propertyId' | 'formHeader'
> &
  CampaignBottomBarProps &
  React.PropsWithChildren<{
    onSubmit: () => void;
    onClickWhatsappButton?: WhatsappContactProps['onClickWhatsappButton'];
    onClickShowPhoneButton: () => void;
    sellerWhatsapp?: WhatsappModalProps['sellerWhatsapp'];
  }> &
  Pick<ShowPhoneModalProps, 'sellerName' | 'sellerLogo' | 'sellerPhone'>;

const MobileContactForm: React.FC<MobileContactFormProps> = ({
  campaignBarText,
  campaignLink,
  campaignLinkText,
  children,
  sellerLogo,
  onClickShowPhoneButton,
  onClickWhatsappButton,
  onSubmit,
  sellerName,
  sellerPhone,
  sellerWhatsapp,
  propertyId,
  formHeader
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('property');
  const contactText = t('contact');

  const state = useSelector(
    (state: RootState) => ({
      isShowPhoneFormSent: state.contactForm.isShowPhoneFormSent,
      source: state.contactForm.source
    }),
    shallowEqual
  );
  const handleOnOpen = React.useCallback(
    (source: typeof state.source) => {
      dispatch(setMobileModal({ isMobileModalOpen: true, source }));
    },
    [dispatch, state]
  );

  const handleOnClose = React.useCallback(() => {
    dispatch(setIsMobileModalOpen(false));
  }, [dispatch]);

  const handleOnClickShowPhone = React.useCallback(() => {
    handleOnOpen('showPhone');
  }, [handleOnOpen]);

  const handleOnClickContact = React.useCallback(() => {
    handleOnOpen('contactForm');
  }, [handleOnOpen]);

  const handleOnClickWhatsapp = React.useCallback(() => {
    handleOnOpen('whatsapp');
  }, [handleOnOpen]);

  return (
    <React.Fragment>
      <CampaignBottomBar
        campaignLink={campaignLink}
        campaignLinkText={campaignLinkText}
        campaignBarText={campaignBarText}
      >
        <Row>
          <Col>
            <ButtonWrapper>
              <Button
                fullWidth
                onClick={handleOnClickContact}
                type='button'
              >
                {contactText}
              </Button>
              {sellerPhone && (
                <PhoneButton
                  color='secondary'
                  disabled={state.isShowPhoneFormSent}
                  onClick={handleOnClickShowPhone}
                  type='button'
                  variant='outlined'
                >
                  <PhoneIcon />
                </PhoneButton>
              )}
              {sellerWhatsapp && onClickWhatsappButton && (
                <WhatsappButton
                  type='button'
                  onClick={handleOnClickWhatsapp}
                />
              )}
            </ButtonWrapper>
          </Col>
        </Row>
      </CampaignBottomBar>
      <Portal>
        <ContactModal
          onClose={handleOnClose}
          onSubmit={onSubmit}
        >
          {children}
        </ContactModal>

        <ShowPhoneModal
          sellerLogo={sellerLogo}
          onClose={handleOnClose}
          onSubmit={onClickShowPhoneButton}
          sellerName={sellerName}
          sellerPhone={sellerPhone}
        />

        {sellerWhatsapp && onClickWhatsappButton && (
          <WhatsappModal
            onClose={handleOnClose}
            propertyId={propertyId}
            sellerWhatsapp={sellerWhatsapp}
            onClickWhatsappButton={onClickWhatsappButton}
            formHeader={formHeader}
          />
        )}
      </Portal>
    </React.Fragment>
  );
};

export default MobileContactForm;
